import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const VALUES = [
  {
    title: "Innovation, Applied",
    body: "We work with modern, efficient LED systems and thoughtful controls — technology chosen for the facility, not for the spec sheet.",
  },
  {
    title: "Professional Coordination",
    body: "Fairlume never installs lighting ourselves. We source professional systems and recommend qualified, independent installation contractors — on-site work is always performed by licensed professionals under their own agreements.",
  },
  {
    title: "Customer-Focused Solutions",
    body: "Every plan starts with your objectives — playable hours, guest experience, operational efficiency — and is built around your site, your power, and your budget.",
  },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="pt-44 pb-20 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <Eyebrow>About Fairlume</Eyebrow>
          <h1 className="font-display font-bold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            Lighting the Next<br />
            <span className="text-[#A7D7A0]">Opportunity.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-lg sm:text-xl text-white/80 leading-relaxed max-w-3xl">
            Fairlume is a modern commercial lighting solutions company focused on helping
            businesses make more of their facilities through efficient, thoughtfully planned
            illumination.
          </p>
        </Reveal>
      </section>

      <section className="py-16 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-muted-green text-base leading-relaxed">
                We believe the most valuable hours at a golf course, range, or sports facility
                may be the ones after sunset. Our work is to make those hours possible — with
                lighting planned around how people actually play, practice, and gather.
              </p>
              <p className="mt-6 text-muted-green text-base leading-relaxed">
                Based in Arizona and built for facilities nationwide, Fairlume pairs careful
                project planning with quality-sourced equipment and trusted contractor
                recommendations — so owners and operators get a single, guided path from
                first conversation to first night under the lights.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative rounded-md overflow-hidden aspect-[4/3] grain">
                <img
                  src="https://images.pexels.com/photos/3195642/pexels-photo-3195642.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Professionally illuminated outdoor property at night"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-[#0B0D0C] border-y hairline">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <Reveal>
            <Eyebrow>How We Work</Eyebrow>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 rounded-md overflow-hidden mt-10">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.12} className="h-full">
                <div className="bg-[#101214] p-8 sm:p-10 h-full">
                  <span className="font-mono-tech text-xs text-[#A7D7A0] tracking-[0.25em]">0{i + 1}</span>
                  <h3 className="font-display font-semibold text-xl tracking-tight mt-5">{v.title}</h3>
                  <p className="mt-4 text-muted-green text-sm leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/quote"
              data-testid="about-quote-cta"
              className="mt-14 inline-flex items-center gap-3 bg-[#A7D7A0] text-[#101214] font-semibold px-8 py-4 rounded-sm hover:bg-white transition-colors duration-300 group"
            >
              Start a Conversation
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
